// src/CompanyRegister/Step6.js
import React, { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const SectionHeader = ({ title, onEdit }) => (
  <div className="flex items-center justify-between">
    <label className="text-sm font-semibold text-foreground">{title}</label>
    {onEdit && (
      <Button type="button" variant="outline" size="sm" onClick={onEdit}>
        Editar
      </Button>
    )}
  </div>
);

const Step6 = ({
  onSubmit,
  onBack,
  onEdit,
  formData,
  userData,
  errorMessage,
}) => {
  const [area, setArea] = useState(formData?.area || "");
  const [puesto, setPuesto] = useState(formData?.puesto || "");

  const handleConfirm = () => {
    if (!area || !puesto) {
      toast.warning("Por favor completa el área y el puesto antes de enviar.");
      return;
    }
    onSubmit({ area, puesto });
  };

  const handleBack = () => {
    onBack({ area, puesto });
  };

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <h3 className="text-xl font-semibold text-foreground">Paso 6: Revisión y Confirmación</h3>

        {errorMessage && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
            <p className="text-sm font-medium text-destructive">⚠️ Error: {errorMessage}</p>
          </div>
        )}

        <div className="space-y-6 text-sm text-foreground">
          <div className="space-y-1.5">
            <SectionHeader title="Información del Responsable:" />
            <p>
              <strong>Nombre y Apellido:</strong> {userData.nombre}{" "}
              {userData.apellido}
            </p>
            <p>
              <strong>Celular:</strong> {userData.celular || "No proporcionado"}
            </p>
            <p>
              <strong>Correo:</strong> {userData.correo}
            </p>
            <div className="grid gap-3 pt-2 sm:grid-cols-2">
              <div>
                <label htmlFor="area" className="mb-1.5 block text-sm font-medium text-foreground">
                  Área:
                </label>
                <Input
                  type="text"
                  id="area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder="Ventas"
                  maxLength={200}
                />
              </div>
              <div>
                <label htmlFor="puesto" className="mb-1.5 block text-sm font-medium text-foreground">
                  Puesto:
                </label>
                <Input
                  type="text"
                  id="puesto"
                  value={puesto}
                  onChange={(e) => setPuesto(e.target.value)}
                  placeholder="Gerente de Ventas"
                  maxLength={200}
                />
              </div>
            </div>
          </div>

          <hr className="border-border" />

          <div className="space-y-1.5">
            <SectionHeader title="Información General de la Empresa:" onEdit={() => onEdit(1)} />
            <p>
              <strong>Nombre de la Empresa:</strong> {formData.nombreEmpresa}
            </p>
            <p>
              <strong>RUC:</strong> {formData.ruc}
            </p>
            <p>
              <strong>Dirección:</strong> {formData.direccion}
            </p>
            <p>
              <strong>Sector:</strong> {formData.sector}
            </p>
          </div>

          <hr className="border-border" />

          <div className="space-y-1.5">
            <SectionHeader title="Detalles Adicionales de la Empresa:" onEdit={() => onEdit(3)} />
            <p>
              <strong>Descripción:</strong> {formData.descripcion}
            </p>
            <p>
              <strong>Número de empleados:</strong> {formData.numeroEmpleados}
            </p>
            <p>
              <strong>Certificaciones:</strong>{" "}
              {formData.certificaciones?.join(", ")}
            </p>
          </div>

          <hr className="border-border" />

          <div className="space-y-1.5">
            <SectionHeader title="Productos y Servicios Ofrecidos:" onEdit={() => onEdit(4)} />
            <div>
              <strong>Productos:</strong>
              {formData.productos?.length > 0 ? (
                <ul className="ml-4 list-disc">
                  {formData.productos.map((producto, index) => (
                    <li key={index}>{producto}</li>
                  ))}
                </ul>
              ) : (
                <p>No se han agregado productos.</p>
              )}
            </div>
            <div>
              <strong>Servicios:</strong>
              {formData.servicios?.length > 0 ? (
                <ul className="ml-4 list-disc">
                  {formData.servicios.map((servicio, index) => (
                    <li key={index}>{servicio}</li>
                  ))}
                </ul>
              ) : (
                <p>No se han agregado servicios.</p>
              )}
            </div>
          </div>

          <hr className="border-border" />

          <div className="space-y-1.5">
            <SectionHeader title="Zonas de Operación:" onEdit={() => onEdit(5)} />
            <p>
              <strong>Ciudades: </strong>
              {formData.operacionGeografica?.join(", ")}
            </p>
          </div>
        </div>

        <div className="space-y-1.5 text-sm text-foreground">
          <SectionHeader title="Contacto:" onEdit={() => onEdit(2)} />
          <p>
            <strong>Página Web:</strong>{" "}
            {formData.paginaWeb || "No proporcionada"}
          </p>
          <p>
            <strong>Teléfono de la empresa:</strong>{" "}
            {formData.telefonoEmpresa || "No proporcionado"}
          </p>

          <p>
            <strong>Lista de Contactos:</strong>
          </p>
          {formData.informacionContacto?.length > 0 ? (
            <ul className="ml-4 list-disc">
              {formData.informacionContacto.map((contact, index) => (
                <li key={index}>
                  {`${contact.nombre} - Cel: ${contact.telefono} - Email: ${contact.email}`}
                </li>
              ))}
            </ul>
          ) : (
            <p>No se han agregado contactos.</p>
          )}

          <p>
            <strong>Redes sociales:</strong>
          </p>
          {formData.redesSociales?.length > 0 ? (
            <ul className="ml-4 list-disc">
              {formData.redesSociales.map((social, index) => (
                <li key={index}>{social.url}</li>
              ))}
            </ul>
          ) : (
            <p>No se han agregado redes sociales.</p>
          )}
        </div>

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={handleBack}>
            Retroceder
          </Button>
          <Button type="button" onClick={handleConfirm}>
            Confirmar y Enviar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Step6;
